import { defineComponent as _defineComponent } from 'vue'
import {computed, inject, Ref, ref, watch} from "vue";
import {debounce} from "@/utils/Helpers";
import CollapsableCard from "@/components/bootstrap/CollapsableCard.vue";
import {LabelLanguage} from "@/models/NutritionLabels/LabelLanguage";
import {useTranslations} from "@/hooks/useTranslations";
import FreeTextPlaceholder from "@/models/NutritionLabels/FreeTextPlaceholder";
import {FreeTextAttributeType} from "@/models/NutritionLabels/v2/enums/FreeTextAttributeType";
import {splitLanguages} from "@/models/NutritionLabels/v2/Helpers";
import {vSelectOptionBase} from "@/typings/common";


export default /*#__PURE__*/_defineComponent({
  __name: 'FreeTextFieldsConfiguration',
  props: {
    shouldExpand: { type: Boolean },
    language: null,
    freeTextPlaceholders: null,
    selectedLabelType: null,
    stepLabel: { default: "3" }
  },
  emits: ["update:freeTextPlaceholders", "fetch-preview"],
  setup(__props: any, { emit }) {

const props = __props as { shouldExpand: boolean, language: keyof typeof LabelLanguage, freeTextPlaceholders: FreeTextPlaceholder[], selectedLabelType: vSelectOptionBase<string>, stepLabel: string };

const fetchPreview: () => void = inject(
  "bopFetchPreview",
  () => console.warn("No fetch preview provided")
);

// Props


const translations = useTranslations();

// Emits


// Local copy to avoid mutating props directly
const localPlaceholders: Ref<FreeTextPlaceholder[]> = ref(structuredClone(props.freeTextPlaceholders) || []);

const labelsToHideSecondaryLangServingSize = ["CannabisLabel"];
const shouldHideSecondaryLanguageServingSize = computed(
  () => labelsToHideSecondaryLangServingSize.includes(props.selectedLabelType.value) && isBilingual.value
);

const isBilingual = computed(() => {
  const language = LabelLanguage[props.language];
  return language === LabelLanguage.BilingualEnglishFrenchEnglishFirst
    || language === LabelLanguage.BilingualEnglishFrenchFrenchFirst
    || language === LabelLanguage.BilingualEnglishSpanishEnglishFirst
    || language === LabelLanguage.BilingualEnglishSpanishSpanishFirst;
});

const languageHeaders = computed(() => {
  return splitLanguages(LabelLanguage[props.language]);
});

function shouldShowLanguageLabel(placeholder: FreeTextPlaceholder) {
  if (
    labelsToHideSecondaryLangServingSize.includes(props.selectedLabelType.value)
    && placeholder.displayText === 'HouseholdMeasurement'
  ) {
    return false;
  }

  return isBilingual.value;
}
function shouldDisplayInput(placeholder: FreeTextPlaceholder, languageIndex: number) {
  if (placeholder.displayText !== 'HouseholdMeasurement') {
    return true;
  }

  if (placeholder.displayText == 'HouseholdMeasurement' && !shouldHideSecondaryLanguageServingSize.value) {
    return true;
  }

  return placeholder.displayText == 'HouseholdMeasurement'
    && shouldHideSecondaryLanguageServingSize.value
    && languageIndex < 1;
}

function getInputType(placeholder: FreeTextAttributeType) {
  switch (placeholder) {
  case FreeTextAttributeType.CAUTIONARY_MESSAGES:
    return "textarea";
  case FreeTextAttributeType.MANUFACTURER_NAME_AND_ADDRESS:
    return "textarea";
  case FreeTextAttributeType.PREPARATION_INSTRUCTIONS:
    return "textarea";
  case FreeTextAttributeType.SERVINGS_PER_CONTAINER:
    return "text";
  default:
    return "text";
  }
}

function getLanguageName(language: LabelLanguage) {
  return translations.value.get(LabelLanguage[language]);
}

// Watch for parent changes
watch(
  () => props.freeTextPlaceholders,
  (newVal) => {
    localPlaceholders.value = structuredClone(newVal);
  },
  { deep: true }
);

// On input change, emit updates and trigger debounced preview
function onInputChange() {
  const normalized = normalizeFreeTextFields(localPlaceholders.value);

  emit("update:freeTextPlaceholders", normalized);
  // Debounced preview event
  debounce(fetchPreview, 1000, "back-of-package-config-label-update");
}

function normalizeFreeTextFields(
  placeholders: FreeTextPlaceholder[]
): FreeTextPlaceholder[] {
  return placeholders.map((p) => ({
    ...p,
    content: p.content.filter((v) => v?.trim().length > 0)
  }));
}

return { __sfc: true,fetchPreview, props, translations, emit, localPlaceholders, labelsToHideSecondaryLangServingSize, shouldHideSecondaryLanguageServingSize, isBilingual, languageHeaders, shouldShowLanguageLabel, shouldDisplayInput, getInputType, getLanguageName, onInputChange, normalizeFreeTextFields, CollapsableCard }
}

})